import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock,
  CheckCircle2,
  XCircle,
  Sparkles,
  Play,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";
import metaIconAsset from "@/assets/meta.png.asset.json";
const metaIcon = metaIconAsset.url;

import creative1 from "@/assets/creatives/creative-1.webp";
import creative2 from "@/assets/creatives/creative-2.webp";
import creative3 from "@/assets/creatives/creative-3.webp";
import creative4 from "@/assets/creatives/creative-4.webp";
import creative5 from "@/assets/creatives/creative-5.webp";
import creative6 from "@/assets/creatives/creative-6.webp";
import creative7 from "@/assets/creatives/creative-7.webp";
import creative8 from "@/assets/creatives/creative-8.webp";

/* ───── Scroll-in animation ───── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function AnimateIn({
  children,
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

/* ───── DATA ───── */

const setupItems = [
  "Auditoría inicial de tu cuenta publicitaria",
  "Configuración de Meta Business Manager",
  "Configuración de portfolio y activos",
  "Instalación y configuración del píxel de Meta",
  "Configuración de eventos personalizados y conversiones",
  "Investigación de audiencias y públicos objetivo",
  "Creación y lanzamiento de tu primera campaña",
];

const monthlyItems = [
  { bold: "1 campaña activa", rest: " gestionada y optimizada" },
  { bold: "3 creativos estáticos", rest: " (diseños para anuncios)" },
  { bold: "1 creativo en vídeo", rest: " para anuncios" },
  { bold: "Test A/B", rest: " para identificar qué funciona mejor" },
  { bold: "Optimización continua", rest: " de públicos, pujas y segmentación" },
  { bold: "Reporte mensual", rest: " con todos los resultados" },
];

const notIncluded = [
  "La inversión publicitaria (el presupuesto de anuncios lo pagas directamente desde tu cuenta a Meta)",
  "Producción de vídeo profesional con cámara y equipo",
  "Gestión de comentarios y mensajes de los anuncios",
  "Gestión orgánica de tus redes sociales (eso es el servicio de Gestión de Redes Sociales)",
  "Diseño de landing pages (servicio aparte)",
];

const processSteps = [
  { n: "01", title: "Contratas el servicio", desc: "Eliges Meta Ads en tu plan." },
  { n: "02", title: "Nos das acceso", desc: "Conectas tu cuenta publicitaria y nos compartes la información necesaria." },
  { n: "03", title: "Setup en 14 días", desc: "Configuramos todo y lanzamos tu primera campaña." },
  { n: "04", title: "Optimización continua", desc: "Gestionamos y mejoramos tu campaña durante el mes." },
  { n: "05", title: "Reporte mensual", desc: "Recibes el informe el día de tu ciclo de facturación." },
];


const staticCreatives = [
  creative1,
  creative2,
  creative3,
  creative4,
  creative5,
  creative6,
  creative7,
  creative8,
];

const videoCreatives = [
  "/videos/creative-video-2.mp4",
  "/videos/creative-video-4.mp4",
  "/videos/creative-video-5.mp4",
  "/videos/creative-video-6.mp4",
  "/videos/creative-video-7.webm",
];

/* ───── COMPONENT ───── */
const MetaAdsDemo = () => {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState<string | null>(null);

  return (
    <div className="space-y-28 overflow-hidden">
      {/* ━━━ 1. HERO ━━━ */}
      <ServiceHero
        eyebrow="Publicidad Digital"
        title={
          <>
            Publicidad en <Highlight>Meta Ads</Highlight>
          </>
        }
        description={
          <>
            Campañas en Facebook e Instagram diseñadas para vender. Gestión
            profesional, optimización continua y reportes claros{" "}
            <strong className="text-foreground font-semibold">cada mes.</strong>
          </>
        }
        primaryCta={{ label: "Contratar Meta Ads", href: "/#pricing" }}
        secondaryCta={{ label: "Agendar reunión", href: "/#contact" }}
        decoration={
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-2xl bg-card border border-border shadow-md flex items-center justify-center"
              style={{ animation: "float 6s ease-in-out infinite" }}
            >
              <img src={metaIcon} alt="Meta" className="w-9 h-9 object-contain" />
            </div>
            <div className="text-sm text-muted-foreground font-body">
              Facebook e Instagram Ads
            </div>
          </div>
        }
        includes={["Campaña", "Creativos", "Vídeo", "Test A/B", "Optimización", "Reporte"]}
      />

      <IncludesExcludes
        includes={[
          "1 campaña activa gestionada al mes",
          "3 creativos estáticos + 1 creativo en vídeo",
          "Vídeo producido con IA + stock + narración, o guiones para que grabes tú y nosotros editamos",
          "Test A/B",
          "Optimización continua de públicos, pujas y segmentación",
          "Setup inicial: cuenta, Business Manager, píxel, eventos y conversiones",
          "Informe mensual el día de tu ciclo de facturación",
        ]}
        excludes={[
          "Inversión publicitaria (la pagas tú; recomendamos mínimo 300€/mes)",
          "Producción de vídeo profesional con cámara y equipo",
          "Gestión de comentarios y mensajes de los anuncios",
          "Tandas de revisión en creativos (publicación directa, optimizamos por rendimiento)",
          "Diseño de landing pages",
        ]}
      />


      {/* ━━━ 3 + 4. SETUP & MENSUAL ━━━ */}
      <section className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Setup */}
          <AnimateIn>
            <div className="h-full rounded-3xl border border-border bg-card p-8 md:p-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-5">
                <CalendarClock size={14} />
                Primeros 14 días
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-3">
                Puesta en marcha
              </h3>
              <p className="text-muted-foreground mb-6">
                Al contratar el servicio, durante los primeros 14 días dejamos
                toda tu cuenta publicitaria lista y lanzamos tu primera campaña.
              </p>
              <ul className="space-y-3">
                {setupItems.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm">
                    <CheckCircle2
                      size={18}
                      className="text-primary shrink-0 mt-0.5"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimateIn>

          {/* Mensual */}
          <AnimateIn delay={0.08}>
            <div className="h-full rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card p-8 md:p-10 shadow-lg shadow-primary/5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium mb-5">
                <Sparkles size={14} />
                Cada mes
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-3">
                Tu servicio mes a mes
              </h3>
              <p className="text-muted-foreground mb-6">
                Cada mes trabajamos de forma continua en tu cuenta para mejorar
                resultados. Esto es exactamente lo que incluye tu suscripción:
              </p>
              <ul className="space-y-3">
                {monthlyItems.map((item) => (
                  <li
                    key={item.bold}
                    className="flex items-start gap-3 text-sm"
                  >
                    <CheckCircle2
                      size={18}
                      className="text-primary shrink-0 mt-0.5"
                    />
                    <span>
                      <strong className="font-semibold">{item.bold}</strong>
                      {item.rest}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* ━━━ 9. EJEMPLOS DE CREATIVOS ━━━ */}
      <section className="bg-muted/30 py-20 -mx-4 md:-mx-8 px-4 md:px-8 rounded-[2.5rem]">
        <div className="max-w-7xl mx-auto">
          <AnimateIn>
            <div className="text-center mb-12">
              <Badge variant="secondary" className="mb-4">
                Nuestro trabajo
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                Ejemplos de creativos que hemos hecho
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Una muestra del tipo de creativos estáticos y vídeos que
                producimos para campañas de Meta Ads. Cada pieza está diseñada
                para captar atención y convertir.
              </p>
            </div>
          </AnimateIn>

          {/* Estáticos */}
          <AnimateIn>
            <h3 className="font-display text-2xl mb-6 text-center md:text-left">
              Creativos estáticos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {staticCreatives.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(src)}
                  className="group relative aspect-square overflow-hidden rounded-2xl border border-border bg-card shadow-sm hover:shadow-xl transition-all duration-500"
                >
                  <img
                    src={src}
                    alt={`Creativo estático ${i + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </button>
              ))}
            </div>
          </AnimateIn>

          {/* Vídeos */}
          <AnimateIn>
            <h3 className="font-display text-2xl mb-6 text-center md:text-left">
              Creativos en vídeo
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {videoCreatives.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveVideo(src)}
                  className="group relative aspect-[9/16] overflow-hidden rounded-2xl border border-border bg-card shadow-sm hover:shadow-xl transition-all duration-500"
                >
                  <video
                    src={src}
                    className="w-full h-full object-cover pointer-events-none"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <div className="absolute inset-0 bg-background/20 flex items-center justify-center group-hover:bg-background/30 transition-colors">
                    <Play className="w-12 h-12 text-primary-foreground bg-primary/90 rounded-full p-3 shadow-lg group-hover:scale-110 transition-transform" />
                  </div>
                </button>
              ))}
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* ━━━ 5. SOBRE LOS CREATIVOS (DESTACADO) ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <div className="relative rounded-3xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 md:p-12 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-primary text-xs font-semibold mb-5">
                <ShieldCheck size={14} />
                Importante
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-4">
                Cómo funcionan los creativos
              </h3>
              <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed">
                <p>
                  Los creativos (3 estáticos + 1 vídeo){" "}
                  <strong className="text-foreground">
                    no se renuevan automáticamente cada mes
                  </strong>
                  . Se actualizan y rotan{" "}
                  <strong className="text-foreground">
                    cuando la campaña lo necesita
                  </strong>{" "}
                  según su rendimiento.
                </p>
                <p>
                  Nuestro criterio es siempre el resultado: si un creativo está
                  funcionando bien, lo mantenemos activo para no perder
                  rendimiento. Si detectamos fatiga publicitaria o caída de
                  resultados, renovamos los creativos.{" "}
                  <strong className="text-foreground">
                    Optimizamos en función de lo que mejor funciona para tu
                    negocio, no por cumplir una cuota mensual.
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ 6. QUÉ NO INCLUYE ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">
              Transparencia total
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Qué no incluye este servicio
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Para que todo quede claro desde el principio, estos puntos quedan
              fuera del servicio.
            </p>
          </div>
        </AnimateIn>

        <div className="max-w-3xl mx-auto space-y-3">
          {notIncluded.map((item, i) => (
            <AnimateIn key={item} delay={i * 0.05}>
              <div className="flex items-start gap-3 p-5 rounded-2xl border border-border bg-card">
                <XCircle
                  size={20}
                  className="text-destructive shrink-0 mt-0.5"
                />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ 7. CÓMO TRABAJAMOS ━━━ */}
      <section className="max-w-6xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">
              Nuestro proceso
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Cómo trabajamos
            </h2>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-5 gap-4 relative">
          {processSteps.map((step, i) => (
            <AnimateIn key={step.n} delay={i * 0.06}>
              <div className="h-full rounded-2xl bg-card border border-border p-6 hover:border-primary/30 hover:shadow-md transition-all relative">
                <div className="font-display text-4xl text-primary/30 mb-3">
                  {step.n}
                </div>
                <h4 className="font-semibold mb-2 text-sm">{step.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {step.desc}
                </p>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>


      {/* ━━━ 10. CTA FINAL ━━━ */}
      <section className="max-w-4xl mx-auto px-4">
        <AnimateIn>
          <div className="relative rounded-3xl bg-gradient-to-br from-primary via-primary/90 to-primary/70 p-10 md:p-16 text-center text-primary-foreground overflow-hidden shadow-2xl shadow-primary/20">
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute -top-20 -left-20 w-64 h-64 bg-white/30 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <h2 className="font-display text-3xl md:text-5xl uppercase mb-4 leading-tight">
                ¿Listo para empezar a vender con Meta Ads?
              </h2>
              <p className="text-lg md:text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                Elige tu plan y lanzamos tu primera campaña en los próximos 14
                días.
              </p>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-background text-foreground font-semibold hover:scale-105 transition-transform shadow-lg"
              >
                Contratar ahora
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ Dialogs ━━━ */}
      <Dialog open={!!activeVideo} onOpenChange={(o) => !o && setActiveVideo(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden bg-background border-border rounded-2xl [&>button]:text-primary-foreground [&>button]:bg-background/60 [&>button]:rounded-full [&>button]:p-1">
          {activeVideo && (
            <video
              src={activeVideo}
              className="w-full aspect-[9/16] object-cover"
              controls
              autoPlay
              playsInline
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!activeImage} onOpenChange={(o) => !o && setActiveImage(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden bg-background border-border rounded-2xl [&>button]:text-primary-foreground [&>button]:bg-background/60 [&>button]:rounded-full [&>button]:p-1">
          {activeImage && (
            <img src={activeImage} alt="Creativo" className="w-full h-auto" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetaAdsDemo;
